import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../services/api';
import { EMAIL_INPUT_PATTERN, EMAIL_INPUT_TITLE, isValidEmailAddress } from '../utils/emailValidation';
import './Signup.css';
import './RequestForms.css';

const valueProvided = (value, type) => {
  if (type === 'checkbox') return Boolean(value);
  if (type === 'file') {
    if (typeof File !== 'undefined' && value instanceof File) return true;
    if (Array.isArray(value)) return value.length > 0;
  }
  if (type === 'city_selector') {
    return value && typeof value === 'object' && Boolean(value.city);
  }
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  return String(value).trim().length > 0;
};

const normalizeStatusMessage = (error) => {
  if (Array.isArray(error?.response?.data?.meta?.errors) && error.response.data.meta.errors.length > 0) {
    return error.response.data.meta.errors.map((item) => item?.message).filter(Boolean).join(' ');
  }

  return error?.response?.data?.error || 'Echec de la soumission. Veuillez reessayer.';
};

const RequestForms = () => {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [responses, setResponses] = useState({});
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitResult, setSubmitResult] = useState(null);
  const explicitSubmitIntentRef = useRef(0);
  const [formResetSeed, setFormResetSeed] = useState(0);
  const [cityCatalog, setCityCatalog] = useState(null);

  useEffect(() => {
    const loadCityCatalog = async () => {
      try {
        const response = await api.forms.getPublicCityCatalog();
        setCityCatalog(response?.data?.data || null);
      } catch (err) {
        console.error('Failed to load city catalog:', err);
      }
    };
    loadCityCatalog();
  }, []);

  useEffect(() => {
    const loadForm = async () => {
      if (!slug) {
        setForm(null);
        setError('Formulaire introuvable.');
        return;
      }

      try {
        setLoading(true);
        setError('');
        const response = await api.forms.getPublicBySlug(slug);
        setForm(response?.data?.data || null);
      } catch (fetchError) {
        console.error('Failed to fetch public form:', fetchError);
        setForm(null);
        setError(fetchError?.response?.status === 404
          ? 'Ce formulaire n\'est pas disponible.'
          : 'Impossible de charger ce formulaire pour le moment.');
      } finally {
        setLoading(false);
      }
    };

    loadForm();
  }, [slug]);

  useEffect(() => {
    setResponses({});
    setCurrentPageIndex(0);
    setSubmitError('');
    setSubmitResult(null);
    setFormResetSeed((prev) => prev + 1);
  }, [form?.slug]);

  const pages = useMemo(
    () => (Array.isArray(form?.schema?.pages) ? form.schema.pages : []),
    [form]
  );

  const allFields = useMemo(() => {
    const fields = [];
    (Array.isArray(pages) ? pages : []).forEach((page) => {
      (Array.isArray(page?.sections) ? page.sections : []).forEach((section) => {
        (Array.isArray(section?.fields) ? section.fields : []).forEach((field) => {
          if (field?.key) fields.push(field);
        });
      });
    });
    return fields;
  }, [pages]);

  const currentPage = pages[currentPageIndex] || null;

  const updateResponse = (key, value) => {
    setResponses((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const validatePage = (page) => {
    const errors = [];
    const sections = Array.isArray(page?.sections) ? page.sections : [];

    for (const section of sections) {
      const fields = Array.isArray(section?.fields) ? section.fields : [];
      for (const field of fields) {
        if (!field?.required) continue;
        const value = responses[field.key];
        if (!valueProvided(value, field.type)) {
          errors.push(`${field.label || field.key} est obligatoire.`);
          continue;
        }

        if (String(field.type || '').toLowerCase() === 'email' && !isValidEmailAddress(value)) {
          errors.push(`${field.label || field.key} doit contenir une adresse email valide avec @.`);
        }
      }
    }

    return errors;
  };

  const goNext = () => {
    explicitSubmitIntentRef.current = 0;
    if (!currentPage) return;
    const pageErrors = validatePage(currentPage);
    if (pageErrors.length > 0) {
      setSubmitError(pageErrors[0]);
      return;
    }

    setSubmitError('');
    setCurrentPageIndex((index) => Math.min(pages.length - 1, index + 1));
  };

  const goPrevious = () => {
    explicitSubmitIntentRef.current = 0;
    setSubmitError('');
    setCurrentPageIndex((index) => Math.max(0, index - 1));
  };

  const markExplicitSubmitIntent = (event) => {
    // Trust only real user interactions (not synthetic/programmatic clicks).
    explicitSubmitIntentRef.current = event?.isTrusted ? Date.now() : 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form) return;

    const intentTimestamp = Number(explicitSubmitIntentRef.current) || 0;
    explicitSubmitIntentRef.current = 0;
    const hasRecentTrustedIntent = intentTimestamp > 0 && (Date.now() - intentTimestamp) <= 2500;

    // Hard guard: never submit unless the explicit confirm button triggered the submit.
    if (!hasRecentTrustedIntent) {
      setSubmitError('Veuillez cliquer sur "Soumettre la demande" pour confirmer votre envoi.');
      return;
    }

    // Only allow final submission from the last page via the explicit submit button.
    if (currentPageIndex < pages.length - 1) {
      setSubmitError('Veuillez finaliser toutes les etapes puis cliquer sur "Soumettre la demande".');
      return;
    }

    const pageErrors = validatePage(currentPage);
    if (pageErrors.length > 0) {
      setSubmitError(pageErrors[0]);
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError('');
      const hasFileFields = allFields.some((field) => String(field?.type || '').toLowerCase() === 'file');
      const payloadResponses = {};
      allFields.forEach((field) => {
        const key = field.key;
        if (!key) return;
        const type = String(field?.type || '').toLowerCase();
        if (type === 'file') return;
        if (Object.prototype.hasOwnProperty.call(responses, key)) {
          payloadResponses[key] = responses[key];
        }
      });

      let payload = { responses: payloadResponses, confirm_submit: true };

      if (hasFileFields) {
        const formData = new FormData();
        formData.append('confirm_submit', 'true');
        formData.append('responses', JSON.stringify(payloadResponses));

        allFields.forEach((field) => {
          const key = field.key;
          if (!key) return;
          const type = String(field?.type || '').toLowerCase();
          if (type !== 'file') return;
          const fileValue = responses[key];
          if (typeof File !== 'undefined' && fileValue instanceof File) {
            formData.append(key, fileValue);
          }
        });

        payload = formData;
      }

      const response = await api.forms.submitPublic(form.slug, payload);
      setSubmitResult(response?.data?.data || null);
    } catch (submitRequestError) {
      console.error('Failed to submit dynamic form:', submitRequestError);
      setSubmitError(normalizeStatusMessage(submitRequestError));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormKeyDown = (event) => {
    if (event.key !== 'Enter') return;

    const targetTag = String(event.target?.tagName || '').toLowerCase();
    const isTextarea = targetTag === 'textarea';
    const isExplicitSubmitButton = targetTag === 'button' && String(event.target?.type || '').toLowerCase() === 'submit';

    // Prevent browser implicit submit on Enter while filling fields.
    if (!isTextarea && !isExplicitSubmitButton) {
      event.preventDefault();
    }
  };

  return (
    <div className="signup-page request-form-page">
      <div className="signup-hero">
        <div className="hero-content">
          <h1>{form?.form_name || 'Demander un logement'}</h1>
          <p>{form?.description || 'Remplissez le formulaire pour soumettre votre demande.'}</p>
        </div>
      </div>

      <div className="signup-container">
        {loading ? (
          <div className="signup-card">
            <p>Chargement du formulaire...</p>
          </div>
        ) : null}

        {!loading && error ? (
          <div className="signup-card">
            <div className="message error">{error}</div>
          </div>
        ) : null}

        {!loading && !error && form ? (
          <>
            {pages.length > 0 ? (
              <div className="progress-bar request-form-progress">
                {pages.map((page, index) => (
                  <React.Fragment key={page.id || `page-${index}`}>
                    <div className={`progress-step ${currentPageIndex >= index ? 'active' : ''} ${currentPageIndex > index ? 'completed' : ''}`}>
                      <div className="step-circle">{index + 1}</div>
                      <span>{page.title || `Etape ${index + 1}`}</span>
                    </div>
                    {index < pages.length - 1 ? <div className="progress-line" /> : null}
                  </React.Fragment>
                ))}
              </div>
            ) : null}

            <div className="signup-card">
              {submitError ? <div className="message error">{submitError}</div> : null}

              {submitResult ? (
                <div className="request-form-success">
                  <h3>Demande envoyee avec succes</h3>
                  <p>Votre demande a ete enregistree. Conservez ce numero de ticket :</p>
                  <div className="request-form-ticket">{submitResult.reference_number || '-'}</div>
                  <p>
                    Vous pouvez suivre votre dossier depuis <Link to="/statut-demandes">Statut des demandes</Link>.
                  </p>
                  <div className="form-navigation">
                    <button
                      type="button"
                      className="btn-submit"
                      onClick={() => {
                        setSubmitResult(null);
                        setResponses({});
                        setCurrentPageIndex(0);
                        setSubmitError('');
                        setFormResetSeed((prev) => prev + 1);
                      }}
                    >
                      Soumettre une autre demande
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className="adeef-uiform">
                  {currentPage ? (
                    <div className="form-step">
                      <h2 className="step-title">{currentPage.title || form.form_name}</h2>
                      {currentPage.description ? <p className="step-subtitle">{currentPage.description}</p> : null}

                      {(Array.isArray(currentPage.sections) ? currentPage.sections : []).map((section, sectionIndex) => (
                        <section key={section.id || `section-${sectionIndex}`} className="request-form-section">
                          {section.title ? (
                            <h3 className="step-title step-title--section request-form-section-title">
                              {section.title}
                            </h3>
                          ) : null}

                          {section.description ? <p className="step-subtitle">{section.description}</p> : null}

                          <div className="form-grid">
                            {(Array.isArray(section.fields) ? section.fields : []).map((field, fieldIndex) => {
                              const key = field.key || `field-${sectionIndex}-${fieldIndex}`;
                              const value = responses[key];
                              const type = String(field.type || 'text').toLowerCase();
                              const requiredSuffix = field.required ? ' *' : '';
                              const fieldLabel = `${field.label || key}${requiredSuffix}`;

                              if (type === 'checkbox') {
                                return (
                                  <label key={field.id || key} className="terms-checkbox request-form-checkbox">
                                    <input
                                      type="checkbox"
                                      checked={Boolean(value)}
                                      onChange={(event) => updateResponse(key, event.target.checked)}
                                    />
                                    <span>{fieldLabel}</span>
                                  </label>
                                );
                              }

                              if (type === 'textarea') {
                                return (
                                  <div key={field.id || key} className="form-group full-width">
                                    <label className="adeef-uiform-field">
                                      <textarea
                                        className="adeef-uiform-input request-form-textarea"
                                        value={String(value || '')}
                                        onChange={(event) => updateResponse(key, event.target.value)}
                                        placeholder=" "
                                        rows={4}
                                      />
                                      <span>{fieldLabel}</span>
                                    </label>
                                  </div>
                                );
                              }

                              if (type === 'select') {
                                return (
                                  <div key={field.id || key} className="form-group">
                                    <label className="adeef-uiform-field" data-filled={value ? 'true' : 'false'}>
                                      <select
                                        className="adeef-uiform-input"
                                        value={String(value || '')}
                                        onChange={(event) => updateResponse(key, event.target.value)}
                                      >
                                        <option value="">Selectionner</option>
                                        {(Array.isArray(field.options) ? field.options : []).map((option) => (
                                          <option key={option} value={option}>{option}</option>
                                        ))}
                                      </select>
                                      <span>{fieldLabel}</span>
                                    </label>
                                  </div>
                                );
                              }

                              if (type === 'file') {
                                const fileValue = (typeof File !== 'undefined' && value instanceof File) ? value : null;
                                return (
                                  <div key={field.id || key} className="form-group full-width">
                                    <label className="adeef-uiform-field" data-filled={fileValue ? 'true' : 'false'}>
                                      <input
                                        key={`${key}-${formResetSeed}`}
                                        className="adeef-uiform-input"
                                        type="file"
                                        accept="application/pdf"
                                        onChange={(event) => {
                                          const file = event.target.files && event.target.files[0];
                                          updateResponse(key, file || null);
                                        }}
                                      />
                                      <span>{fieldLabel}</span>
                                    </label>
                                    {fileValue ? (
                                      <div className="request-form-file-name">Fichier: {fileValue.name}</div>
                                    ) : null}
                                  </div>
                                );
                              }

                              if (type === 'city_selector') {
                                const catalogCities = cityCatalog?.cities || [];
                                const currentResponse = (value && typeof value === 'object') ? value : { city: '', sub_region: '', residences: [] };
                                const selectedCityObj = catalogCities.find((c) => c.name === currentResponse.city);
                                const cityHasSubRegions = Array.isArray(selectedCityObj?.sub_regions) && selectedCityObj.sub_regions.length > 0;
                                const subRegions = cityHasSubRegions ? (selectedCityObj.sub_regions || []) : [];
                                const residences = cityHasSubRegions
                                  ? (subRegions.find((sr) => sr.name === currentResponse.sub_region)?.residences || [])
                                  : (selectedCityObj?.residences || []);

                                const handleCityChange = (newCity) => {
                                  updateResponse(key, { city: newCity, sub_region: '', residences: [] });
                                };

                                const handleSubRegionChange = (newSubRegion) => {
                                  updateResponse(key, { ...currentResponse, sub_region: newSubRegion, residences: [] });
                                };

                                const handleResidenceToggle = (residence) => {
                                  const current = currentResponse.residences || [];
                                  const isSelected = current.includes(residence);
                                  let next;
                                  if (isSelected) {
                                    next = current.filter((r) => r !== residence);
                                  } else if (current.length < 3) {
                                    next = [...current, residence];
                                  } else {
                                    return;
                                  }
                                  updateResponse(key, { ...currentResponse, residences: next });
                                };

                                return (
                                  <div key={field.id || key} className="form-group full-width city-selector-field">
                                    <label className="adeef-uiform-label">{fieldLabel}</label>
                                    <div className="city-selector">
                                      <div className="form-group">
                                        <label className="adeef-uiform-field" data-filled={currentResponse.city ? 'true' : 'false'}>
                                          <select
                                            className="adeef-uiform-input"
                                            value={currentResponse.city || ''}
                                            onChange={(event) => handleCityChange(event.target.value)}
                                          >
                                            <option value="">Selectionner une ville</option>
                                            {catalogCities.map((city) => (
                                              <option key={city.name} value={city.name}>{city.name}</option>
                                            ))}
                                          </select>
                                          <span>Ville</span>
                                        </label>
                                      </div>

                                      {cityHasSubRegions && subRegions.length > 0 && (
                                        <div className="form-group">
                                          <label className="adeef-uiform-field" data-filled={currentResponse.sub_region ? 'true' : 'false'}>
                                            <select
                                              className="adeef-uiform-input"
                                              value={currentResponse.sub_region || ''}
                                              onChange={(event) => handleSubRegionChange(event.target.value)}
                                            >
                                              <option value="">Selectionner une sous-region</option>
                                              {subRegions.map((sr) => (
                                                <option key={sr.name} value={sr.name}>{sr.name}</option>
                                              ))}
                                            </select>
                                            <span>Sous-region</span>
                                          </label>
                                        </div>
                                      )}

                                      {currentResponse.city && !cityHasSubRegions && residences.length > 0 && (
                                        <div className="form-group full-width">
                                          <span className="city-selector-hint">Choisissez jusqu'a 3 residences :</span>
                                          <div className="city-selector-checkboxes">
                                            {residences.map((residence) => (
                                              <label key={residence} className="request-form-checkbox">
                                                <input
                                                  type="checkbox"
                                                  checked={(currentResponse.residences || []).includes(residence)}
                                                  onChange={() => handleResidenceToggle(residence)}
                                                />
                                                <span>{residence}</span>
                                              </label>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                      {cityHasSubRegions && currentResponse.sub_region && residences.length > 0 && (
                                        <div className="form-group full-width">
                                          <span className="city-selector-hint">Choisissez jusqu'a 3 residences :</span>
                                          <div className="city-selector-checkboxes">
                                            {residences.map((residence) => (
                                              <label key={residence} className="request-form-checkbox">
                                                <input
                                                  type="checkbox"
                                                  checked={(currentResponse.residences || []).includes(residence)}
                                                  onChange={() => handleResidenceToggle(residence)}
                                                />
                                                <span>{residence}</span>
                                              </label>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              }

                              return (
                                <div key={field.id || key} className="form-group">
                                  <label className="adeef-uiform-field" data-filled={value ? 'true' : 'false'}>
                                    <input
                                      className="adeef-uiform-input"
                                      type={['number', 'email', 'date', 'tel'].includes(type) ? type : 'text'}
                                      value={value == null ? '' : String(value)}
                                      onChange={(event) => updateResponse(key, event.target.value)}
                                      placeholder=" "
                                      pattern={type === 'email' ? EMAIL_INPUT_PATTERN : undefined}
                                      title={type === 'email' ? EMAIL_INPUT_TITLE : undefined}
                                    />
                                    <span>{fieldLabel}</span>
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        </section>
                      ))}
                    </div>
                  ) : (
                    <div className="message error">Ce formulaire ne contient aucune page configuree.</div>
                  )}

                  <div className="form-navigation">
                    {currentPageIndex > 0 ? (
                      <button
                        type="button"
                        className="btn-prev"
                        onClick={goPrevious}
                        disabled={submitting}
                      >
                        ← Precedent
                      </button>
                    ) : null}

                    {currentPageIndex < pages.length - 1 ? (
                      <button
                        key="next-step-button"
                        type="button"
                        className="btn-next"
                        onClick={goNext}
                        disabled={submitting || !currentPage}
                      >
                        Suivant →
                      </button>
                    ) : (
                      <button
                        key="submit-final-button"
                        type="submit"
                        className="btn-submit"
                        onClick={markExplicitSubmitIntent}
                        disabled={submitting || !currentPage}
                      >
                        {submitting ? 'Soumission...' : 'Soumettre la demande'}
                      </button>
                    )}
                  </div>
                </form>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default RequestForms;
